import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Btn } from "../components/UI";
import { toast } from "sonner";
import { Loader2, HandHeart, XCircle } from "lucide-react";

export default function AcceptInvite() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    api.get(`/political-os/invitations/${token}`)
      .then(r => setInfo(r.data))
      .catch(e => setError(errMsg(e, "This invitation is invalid or has expired")));
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/political-os/invitations/${token}/accept`);
      const label = data.bulk ? `${data.entities.length} campaigns` : data.entity.name;
      toast.success(`Welcome to ${label}`);
      setTimeout(() => { window.location.href = "/dashboard"; }, 800);
    } catch (e) { toast.error(errMsg(e, "Could not accept invitation")); setAccepting(false); }
  };

  return (
    <div className="min-h-screen bg-app text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center space-y-5" data-testid="accept-invite-page">
        {error ? (
          <>
            <XCircle className="w-12 h-12 text-red-400 mx-auto" />
            <div className="font-display text-xl font-bold">Invitation problem</div>
            <p className="text-sm text-slate-400" data-testid="invite-error">{error}</p>
          </>
        ) : !info ? (
          <Loader2 className="w-8 h-8 animate-spin text-cyan-300 mx-auto" />
        ) : (
          <>
            <HandHeart className="w-12 h-12 text-cyan-300 mx-auto" />
            <div className="font-display text-2xl font-bold">
              {info.bulk ? `Join ${info.entities.length} campaigns` : `Join ${info.entity_name}`}
            </div>
            <p className="text-sm text-slate-400">
              {info.invited_by} invited <b className="text-slate-200">{info.email}</b>
              {!info.bulk && (
                <>
                  {" "}as <b className="text-cyan-300">{(info.roles || []).join(", ").replace(/_/g, " ")}</b>
                </>
              )}
              .
            </p>
            {info.bulk && (info.entities || []).length > 0 && (
              <div className="text-left rounded-lg border border-white/10 bg-slate-900/40 p-3 space-y-1.5" data-testid="invite-bulk-list">
                {info.entities.map(e => (
                  <div key={e.entity_id} className="flex items-center justify-between gap-3 text-xs" data-testid={`invite-entity-${e.entity_id}`}>
                    <span className="text-slate-200">{e.entity_name}</span>
                    <span className="text-cyan-300">{(e.roles || []).join(", ").replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
            )}
            {!info.email_matches && (
              <p className="text-xs text-amber-300" data-testid="email-mismatch-warning">
                You&apos;re signed in with a different email — sign in as {info.email} to accept.
              </p>
            )}
            <Btn onClick={accept} disabled={accepting || !info.email_matches} data-testid="accept-invite-btn">
              {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {info.bulk ? "Accept all invitations" : "Accept invitation"}
            </Btn>
          </>
        )}
      </div>
    </div>
  );
}
